// Types mirror sporapi Resources/Admin/Bots/* output. Keep field names in sync —
// when sporapi adds a column, update both BotResource (PHP) and the matching
// interface here.

import { ShorUserInterface } from "./users.types";

// ---- Enums (mirror sporapi BotStatusEnum etc.) ----

export enum BotStatus {
  ACTIVE = 1,
  PAUSED = 2,
  ARCHIVED = 3,
}

export enum BotBillableStatus {
  ACTIVE = 1,
  GRACE = 2,
  DISABLED_UNPAID = 3,
  EXPIRED = 4,
}

export enum BotSubscriptionStatus {
  Incomplete = "incomplete",
  IncompleteExpired = "incomplete_expired",
  Trialing = "trialing",
  Active = "active",
  PastDue = "past_due",
  Canceled = "canceled",
  Unpaid = "unpaid",
  Paused = "paused",
}

export enum BotTaskType {
  CreateFreePost = "create_free_post",
  LikeFeed = "like_feed",
  CommentFeed = "comment_feed",
  ViewFeed = "view_feed",
  ActivityPing = "activity_ping",
  ReadNotifications = "read_notifications",
  ReportViolations = "report_violations",
  // ---- Social graph (added 2026-05-16) ----
  FollowRelevantUsers = "follow_relevant_users",
  UnfollowInactiveUsers = "unfollow_inactive_users",
  WelcomeNewUsers = "welcome_new_users",
  // ---- Reposts / amplification (added 2026-05-16) ----
  RepostFreeFeed = "repost_free_feed",
  QuoteRepostFreeFeed = "quote_repost_free_feed",
  // ---- Comments / discoverability (added 2026-05-16) ----
  LikeRealComments = "like_real_comments",
  MentionRelevantUser = "mention_relevant_user",
  // ---- Events module (added 2026-05-16) ----
  ReactEventMessageContextual = "react_event_message_contextual",
}

export enum BotActionType {
  CreatePost = "create_post",
  EditPost = "edit_post",
  DeletePost = "delete_post",
  ReactivatePost = "reactivate_post",
  LikePost = "like_post",
  UnlikePost = "unlike_post",
  ViewPost = "view_post",
  CommentPost = "comment_post",
  ReplyComment = "reply_comment",
  LikeComment = "like_comment",
  DeleteComment = "delete_comment",
  SendMessage = "send_message",
  DeclineChatRequest = "decline_chat_request",
  ReadNotification = "read_notification",
  DeleteNotification = "delete_notification",
  ReportPost = "report_post",
  ReportUser = "report_user",
  ActivityPing = "activity_ping",
  UpdateProfile = "update_profile",
  // ---- Social graph (added 2026-05-16) ----
  FollowUser = "follow_user",
  UnfollowUser = "unfollow_user",
  // ---- Reposts / amplification (added 2026-05-16) ----
  RepostPost = "repost_post",
  QuoteRepostPost = "quote_repost_post",
  // ---- Events module (added 2026-05-16) ----
  ReactEventMessage = "react_event_message",
}

export enum BotActionStatus {
  SUCCESS = 1,
  FAILED = 2,
  SKIPPED = 3,
  RATE_LIMITED = 4,
}

// ---- Composite shapes ----

export interface BotPersona {
  display_first_name: string;
  display_last_name: string;
  user_type: number; // 1 PF, 2 PJ
  bio: string | null;
  system_prompt_fragment: string;
}

export interface BotLocation {
  country: { id: number | null; name: string | null };
  county: { id: number | null; name: string | null };
  city: { id: number | null; name: string | null };
}

export interface BotInterface {
  id: number;
  name: string;
  status: { value: BotStatus; label: string };
  billable_status: { value: BotBillableStatus; label: string; is_operational: boolean };
  is_operational: boolean;
  persona: BotPersona;
  user: {
    id: number;
    email: string;
    username: string | null;
    first_name: string;
    last_name: string;
    type: number;
    profile_picture: string | null;
    last_activity: string | null;
  } | null;
  location: BotLocation | null;
  categories: { id: number; name: string }[];
  created_by_admin_id: number | null;
  activated_at: string | null;
  disabled_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  task_count?: number;
}

export interface BotTaskInterface {
  id: number;
  bot_id: number;
  // `description` is the long-form admin explanation generated by
  // BotTaskTypeEnum::description() server-side. Optional in the type
  // because older API builds didn't return it; new code should always
  // expect it to be present.
  task_type: { value: BotTaskType; label: string; description?: string };
  config: Record<string, unknown>;
  schedule: BotTaskSchedule;
  priority: number;
  is_enabled: boolean;
  last_run_at: string | null;
  next_run_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface BotTaskSchedule {
  timezone: string;
  active_hours_start: string; // "HH:MM"
  active_hours_end: string;
  weekend_modifier: number;
}

export interface BotSubscriptionInterface {
  id: number;
  stripe_subscription_id: string;
  status: BotSubscriptionStatus;
  is_operational: boolean;
  quantity: number;
  current_period_start: string | null;
  current_period_end: string | null;
  grace_period_ends_at: string | null;
  canceled_at: string | null;
  created_at: string | null;
}

export interface BotPricingQuote {
  quantity: number;
  tier: 1 | 2 | 3;
  unit_price_cents: number;
  subtotal_cents: number;
  vat_percent: number;
  vat_cents: number;
  total_cents: number;
  currency: string;
}

export interface BotBillingDetails {
  company_name: string | null;
  cui: string | null;
  registration_number: string | null;
  address: string | null;
  country_code: string;
  notifications_email: string | null;
  has_stripe_customer: boolean;
  has_connected_account: boolean;
  currency: string;
  vat_percent: number;
  grace_period_days: number;
  cancel_after_grace_days: number;
  bots_enabled: boolean;
}

export interface BotPaymentMethod {
  id: string;
  brand: string | null;
  last4: string | null;
  exp_month: number | null;
  exp_year: number | null;
  is_default: boolean;
}

export interface BotInvoice {
  id: string;
  number: string | null;
  status: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  created_at: string | null;
  period_start: string | null;
  period_end: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

export interface BotInvoicesPage {
  data: BotInvoice[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface BotActionLogInterface {
  id: number;
  bot_id: number;
  task_id: number | null;
  action_type: BotActionType;
  target_type: string | null;
  target_id: number | null;
  status: { value: BotActionStatus; label: string };
  executed_at: string | null;
  latency_ms: number | null;
  error_code: string | null;
  error_message: string | null;
}

export interface BotBillingEventInterface {
  id: number;
  bot_id: number | null;
  event_type: string;
  amount_cents: number | null;
  currency: string | null;
  stripe_event_id: string | null;
  payload: Record<string, unknown> | null;
  occurred_at: string | null;
}

// ---- Stats payloads ----

export interface BotStatsOverview {
  bots_by_status: Record<string, number>;
  bots_by_billable_status: Record<string, number>;
  actions_today: number;
  actions_today_by_type: Record<string, number>;
  success_rate_7d: number;
  posts_created_7d: Record<string, number>;
  top_active_bots_7d: { bot_id: number; name: string; action_count: number }[];
  recent_failures_24h: {
    bot_id: number;
    action_type: string;
    error_code: string | null;
    error_message: string | null;
    executed_at: string;
  }[];
}

export interface BotStatsForBot {
  totals: { actions_24h: number; actions_7d: number; actions_30d: number };
  by_type_30d: Record<
    string,
    { success: number; failed: number; skipped: number; rate_limited: number }
  >;
  success_rate_30d: number;
  avg_latency_ms_by_type: Record<string, number>;
  actions_per_day_30d: { day: string; count: number }[];
  last_action_at: string | null;
  contribution: {
    posts_created: number;
    likes_given: number;
    comments_given: number;
  };
}

// ---- Mutation payloads ----

export interface CreateBotPayload {
  name: string;
  persona: Omit<BotPersona, "user_type"> & { user_type: number };
  target_country_id: number;
  target_county_id: number | null;
  target_city_id: number | null;
  target_category_ids: number[];
  // Optional overrides. username is uniqueness-checked server-side;
  // profile_picture is a base64 data string.
  username?: string;
  profile_picture?: string;
}

export interface UpdateBotPayload {
  id: number;
  name?: string;
  persona?: Partial<BotPersona>;
  username?: string;
  profile_picture?: string;
}

export interface CreateBotTaskPayload {
  bot_id: number;
  task_type: BotTaskType;
  config: Record<string, unknown>;
  schedule?: Partial<BotTaskSchedule>;
  priority?: number;
  is_enabled?: boolean;
}

export interface UpdateBotTaskPayload extends Partial<CreateBotTaskPayload> {
  id: number;
}

export interface UpdateBillingDetailsPayload {
  company_name: string;
  cui: string;
  registration_number: string;
  address: string;
  country_code: string;
  notifications_email: string;
}

export interface BotBillingComposite {
  billing_details: BotBillingDetails;
  subscription: BotSubscriptionInterface | null;
  current_quote: BotPricingQuote;
  has_payment_method: boolean;
}

// Convenience: actor summary type for action log rendering
export type BotActor = ShorUserInterface | null;
